import { useState } from "react";
import { ExternalLink, AlertCircle, CheckCircle2, Trash2, X } from "lucide-react";
import { deleteArticle, getAdminToken, reportBrokenArticle } from "@/lib/api";

const typeBadge = {
    løfte: { label: "Løfte", color: "#186A3B", bg: "#DCF5E4" },
    løgn: { label: "Løgn", color: "#8B1A1A", bg: "#FADDDF" },
    valgflæsk: { label: "Valgflæsk", color: "#7A5A00", bg: "#FFF2D1" },
    skandale: { label: "Skandale", color: "#FFFFFF", bg: "#6B0F8C" },
    generel: { label: "Omtale", color: "#364A7A", bg: "#E5EAF5" },
};

export default function ArticleCard({ article, onDeleted }) {
    const t = typeBadge[article.type] || typeBadge.generel;
    const Icon = article.supports_kept ? CheckCircle2 : AlertCircle;
    const [deleted, setDeleted] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [working, setWorking] = useState(false);
    const isAdmin = !!getAdminToken();

    async function handleDelete(e) {
        e.preventDefault();
        e.stopPropagation();
        setWorking(true);
        try {
            await deleteArticle(article.id);
            setDeleted(true);
            onDeleted?.(article.id);
        } catch (err) {
            alert("Kunne ikke slette artiklen: " + (err?.response?.data?.detail || err?.message || ""));
            setWorking(false);
            setConfirming(false);
        }
    }

    function handleVisit() {
        // Når brugeren klikker, fire-and-forget en passiv server-side check
        // af om URL'en stadig er live. Server-siden sletter kun hvis der
        // returneres 404/410 — ellers ingen handling.
        reportBrokenArticle(article.id)
            .then((result) => {
                if (result?.deleted) {
                    setDeleted(true);
                    onDeleted?.(article.id);
                }
            })
            .catch(() => {});
    }

    if (deleted) return null;

    return (
        <div
            className="relative bg-white border border-[#E5E8F0] rounded-xl p-5 hover-lift group"
            data-testid={`article-card-${article.id}`}
        >
            {isAdmin && !confirming && (
                <button
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setConfirming(true);
                    }}
                    title="Slet artikel (admin)"
                    aria-label="Slet artikel"
                    data-testid={`delete-article-${article.id}`}
                    className="absolute top-3 right-3 p-1.5 rounded-full bg-white border border-[#E5E8F0] text-[#8A94AE] hover:text-[#BB1B2D] hover:border-[#BB1B2D] transition-colors opacity-0 group-hover:opacity-100"
                >
                    <Trash2 className="w-3.5 h-3.5" />
                </button>
            )}
            {isAdmin && confirming && (
                <div
                    className="absolute top-3 right-3 z-10 bg-white border border-[#BB1B2D] rounded-lg p-2 flex items-center gap-2 shadow-md"
                    data-testid={`confirm-delete-${article.id}`}
                >
                    <span className="text-xs text-[#BB1B2D] font-semibold pl-1">Slet?</span>
                    <button
                        onClick={handleDelete}
                        disabled={working}
                        data-testid={`confirm-delete-yes-${article.id}`}
                        className="px-2.5 py-1 rounded-full bg-[#BB1B2D] text-white text-xs font-semibold hover:bg-[#8B1A1A] disabled:opacity-50"
                    >
                        {working ? "Sletter…" : "Ja, slet"}
                    </button>
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setConfirming(false);
                        }}
                        aria-label="Annuller"
                        className="p-1 text-[#4F5A7A] hover:text-[#0B1A3A]"
                    >
                        <X className="w-3.5 h-3.5" />
                    </button>
                </div>
            )}

            <a
                href={article.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
                onClick={handleVisit}
            >
                <div className="flex items-center gap-2 mb-3 pr-10 flex-wrap">
                    <span
                        className="px-2 py-0.5 rounded-full text-xs font-semibold"
                        style={{ color: t.color, backgroundColor: t.bg }}
                    >
                        {t.label}
                    </span>
                    {article.related_politician_name && (
                        <span
                            className={`px-2 py-0.5 rounded-full text-[11px] font-semibold inline-flex items-center gap-1 ${
                                article.related_politician_active === false
                                    ? "bg-[#F1E9D8] text-[#7A5A00] border border-[#E1CD93]"
                                    : "bg-[#E5EAF5] text-royal border border-[#C9D1E5]"
                            }`}
                            data-testid={`article-politician-badge-${article.id}`}
                            title={
                                article.related_politician_active === false
                                    ? "Tidligere folketingsmedlem"
                                    : "Aktivt folketingsmedlem"
                            }
                        >
                            {article.related_politician_active === false && (
                                <span aria-hidden className="mr-0.5">•</span>
                            )}
                            {article.related_politician_active === false
                                ? `Tidl. medlem · ${article.related_politician_name}`
                                : article.related_politician_name}
                        </span>
                    )}
                    <span className="text-xs text-[#8A94AE]">· {article.source}</span>
                    <span className="text-xs text-[#8A94AE]">
                        · {new Date(article.date).toLocaleDateString("da-DK")}
                    </span>
                </div>
                <h4 className="font-serif-dm text-lg text-royal leading-tight mb-2 group-hover:text-danish-red transition-colors">
                    {article.title}
                </h4>
                <p className="text-sm text-[#4F5A7A] leading-relaxed mb-3">
                    {article.excerpt}
                </p>
                <div className="flex items-center justify-between text-xs">
                    <span
                        className={`inline-flex items-center gap-1 font-semibold ${
                            article.supports_kept ? "text-[#186A3B]" : "text-[#BB1B2D]"
                        }`}
                    >
                        <Icon className="w-3.5 h-3.5" />
                        {article.supports_kept
                            ? "Dokumenterer løfte holdt"
                            : "Dokumenterer løftebrud"}
                    </span>
                    <span className="text-royal font-medium inline-flex items-center gap-1 group-hover:gap-1.5 transition-all">
                        Læs kilde <ExternalLink className="w-3.5 h-3.5" />
                    </span>
                </div>
            </a>
        </div>
    );
}
